import { useState, useRef, useCallback, useLayoutEffect } from "react";
import type { RefObject } from "react";

export type Position = { top: number; left: number };

export type BasicPosition = "top" | "bottom" | "left" | "right";

export type ExtendedPosition =
  | BasicPosition
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

/**
 * Calculate element position relative to a trigger element.
 * Supports basic positions (top, bottom, left, right) and extended diagonal positions.
 */
export function calculateElementPosition(
  triggerRect: DOMRect,
  elementRect: DOMRect,
  position: ExtendedPosition,
  offset: number,
): Position {
  const { top, left, width, height, bottom, right } = triggerRect;

  switch (position) {
    // Basic positions - element centered on axis
    case "top":
    case "top-center":
      return {
        top: top - elementRect.height - offset,
        left: left + width / 2 - elementRect.width / 2,
      };
    case "bottom":
    case "bottom-center":
      return {
        top: bottom + offset,
        left: left + width / 2 - elementRect.width / 2,
      };
    case "left":
      return {
        top: top + height / 2 - elementRect.height / 2,
        left: left - elementRect.width - offset,
      };
    case "right":
      return {
        top: top + height / 2 - elementRect.height / 2,
        left: right + offset,
      };

    // Diagonal positions
    case "top-left":
      return {
        top: top - elementRect.height - offset,
        left: left - elementRect.width - offset,
      };
    case "top-right":
      return {
        top: top - elementRect.height - offset,
        left: right + offset,
      };
    case "bottom-left":
      return {
        top: bottom + offset,
        left: left - elementRect.width - offset,
      };
    case "bottom-right":
      return {
        top: bottom + offset,
        left: right + offset,
      };

    default:
      return { top: 0, left: 0 };
  }
}

export interface UsePortalPositionOptions<P extends ExtendedPosition> {
  /** Position of the element relative to trigger */
  position: P;
  /** Offset distance from trigger in pixels */
  offset: number;
  /** Whether the element is visible (controls event listeners) */
  isVisible: boolean;
}

export interface UsePortalPositionResult {
  /** Ref to attach to the trigger wrapper element */
  triggerRef: RefObject<HTMLDivElement | null>;
  /** Ref to attach to the portal element */
  elementRef: RefObject<HTMLDivElement | null>;
  /** Calculated coordinates for absolute positioning (includes scroll offset) */
  coords: Position | null;
  /** Manually trigger position update */
  updatePosition: () => void;
}

/**
 * Hook for managing portal element positioning relative to a trigger element.
 *
 * Handles:
 * - Position calculation based on trigger and element dimensions
 * - Scroll offset for absolute positioning
 * - Resize and scroll event listeners for dynamic repositioning
 *
 * @example
 * ```tsx
 * const { triggerRef, elementRef, coords } = usePortalPosition({
 *   position: "top",
 *   offset: 8,
 *   isVisible: true,
 * });
 *
 * return (
 *   <>
 *     <div ref={triggerRef}>{children}</div>
 *     {createPortal(
 *       <div ref={elementRef} style={{ position: "absolute", top: coords?.top, left: coords?.left }}>
 *         Content
 *       </div>,
 *       document.body
 *     )}
 *   </>
 * );
 * ```
 */
export function usePortalPosition<P extends ExtendedPosition>({
  position,
  offset,
  isVisible,
}: UsePortalPositionOptions<P>): UsePortalPositionResult {
  const [coords, setCoords] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !elementRef.current || !isVisible) return;

    // Handle display: contents - measure first child if wrapper has no dimensions
    let triggerRect = triggerRef.current.getBoundingClientRect();
    if (triggerRect.width === 0 && triggerRect.height === 0) {
      const firstChild = triggerRef.current.firstElementChild;
      if (firstChild) {
        triggerRect = firstChild.getBoundingClientRect();
      }
    }

    const elementRect = elementRef.current.getBoundingClientRect();

    const newPosition = calculateElementPosition(
      triggerRect,
      elementRect,
      position,
      offset,
    );

    // Add scroll offset for absolute positioning in document
    setCoords({
      top: newPosition.top + window.scrollY,
      left: newPosition.left + window.scrollX,
    });
  }, [position, offset, isVisible]);

  // Update position when visibility changes
  useLayoutEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  // Listen for resize and scroll events
  useLayoutEffect(() => {
    if (!isVisible) return;

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition, isVisible]);

  // Listen for transitionend events to recalculate position after CSS transitions
  // This handles cases like panel collapse/expand where elements animate to new positions
  useLayoutEffect(() => {
    if (!isVisible) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      // Only recalculate for relevant properties that affect layout
      if (
        e.propertyName === "width" ||
        e.propertyName === "min-width" ||
        e.propertyName === "max-width" ||
        e.propertyName === "transform" ||
        e.propertyName === "left" ||
        e.propertyName === "right"
      ) {
        requestAnimationFrame(updatePosition);
      }
    };

    document.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      document.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isVisible, updatePosition]);

  return {
    triggerRef,
    elementRef,
    coords,
    updatePosition,
  };
}

export default usePortalPosition;
